/** The forms of the climate risk register that change what a risk *is* (ADR-0230, D-13 and D-09).
 *
 * - **The scale** (`ScaleForm`): an Admin defines the scale a score is read against, and replacing it
 *   rescores nothing - every risk scored on the old one is marked as owing a rescoring (RSK-02).
 * - **Assess again** (`RescoreForm`): a new dated assessment on the same risk. The earlier one stays,
 *   with the scale it was read on; nothing is overwritten.
 * - **Reopen** (`ReopenForm`): a closed risk comes back, with a reason. The closure stays in its history.
 * - **Reword** (`RewordForm`): the way a stored placeholder is fixed by a person.
 *
 * All four follow Q-06 (`useFormChecks`): the button is never greyed for a missing field, pressing it
 * says which box is wrong, beside the box, and a refusal from the server is placed the same way.
 * **A score is words**: a position is chosen by its label and nothing here prints or multiplies one
 * (RSK-01).
 */

import { useState } from "react";

import type { Risk, RiskScale } from "../api/queries";
import {
  useDefineRiskScale,
  useReopenRisk,
  useRescoreRisk,
  useRewordRisk,
} from "../api/queries";
import { FieldError } from "../components/FieldError";
import { WriteFailed } from "../components/Loaded";
import { useFormChecks } from "../components/useFormChecks";
import { criteriaRefusal, triggerRefusal } from "../lib/riskWording";
import styles from "./Screen.module.css";
import own from "./Risks.module.css";

/** The most positions a scale may have, and the fewest: the server's bounds, said before the request. */
const MIN_POSITIONS = 2;
const MAX_POSITIONS = 10;

/** One line per position, lowest first, blank lines ignored. */
function positionsOf(text: string): string[] {
  return text
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line !== "");
}

// ----------------------------------------------------------------- the scale

/** Define the scale, or replace the current one. An Admin's act; the screen offers it to no one else. */
export function ScaleForm({
  scales,
  onDone,
}: {
  scales: ReadonlyArray<RiskScale>;
  onDone: () => void;
}) {
  const current = scales.find((scale) => !scale.superseded);
  const define = useDefineRiskScale();
  const [name, setName] = useState("");
  const [labels, setLabels] = useState("");
  const [refusal, setRefusal] = useState<unknown>(null);
  const [checks, attach] = useFormChecks<HTMLDivElement>();

  async function submit() {
    setRefusal(null);
    const positions = positionsOf(labels);
    const ok = checks.run([
      { field: "name", ok: name.trim() !== "", message: "Give the scale a name, such as Five-point." },
      {
        field: "labels",
        ok: positions.length >= MIN_POSITIONS && positions.length <= MAX_POSITIONS,
        message: `Write between ${MIN_POSITIONS} and ${MAX_POSITIONS} positions, one word or phrase per line, lowest first. You have written ${positions.length}.`,
      },
      {
        field: "labels",
        ok: positions.every((position) => position.length <= 80),
        message: "Keep each position to 80 characters.",
      },
    ]);
    if (!ok) return;
    try {
      await define.mutateAsync({
        name: name.trim(),
        lowest: 1,
        highest: positions.length,
        labels: positions,
        replaces: current?.id ?? null,
      });
      onDone();
    } catch (error) {
      if (!checks.refuse(error)) setRefusal(error);
    }
  }

  return (
    <div className={own.form} ref={attach} data-testid="scale-form">
      {current ? (
        <p className={styles.note} data-testid="scale-current">
          <strong>{current.name}</strong>: {current.labels.map((label, i) => `${i + 1} ${label}`).join(", ")}.
          Replacing it rescores nothing: each risk scored on it is marked as owing a rescoring.
        </p>
      ) : (
        <p className={styles.note}>
          A likelihood of 4 means nothing without the scale it was read against. Define it once; every
          risk is scored on it.
        </p>
      )}
      <label className={styles.field}>
        <span>{current ? "Name of the replacement" : "Name of the scale"}</span>
        <input
          aria-label="Name of the scale"
          {...checks.field("name")}
          value={name}
          onChange={(event) => {
            checks.touch("name");
            setName(event.target.value);
          }}
          placeholder="Five-point"
        />
        <FieldError {...checks.say("name")} />
      </label>
      <label className={styles.field}>
        <span>Positions, lowest first, one per line</span>
        <textarea
          aria-label="Positions of the scale"
          rows={5}
          {...checks.field("labels")}
          value={labels}
          onChange={(event) => {
            checks.touch("labels");
            setLabels(event.target.value);
          }}
          placeholder={"Rare\nUnlikely\nPossible\nLikely\nAlmost certain"}
        />
        <FieldError {...checks.say("labels")} />
      </label>
      <p className={own.rowActions}>
        <button
          type="button"
          className={styles.primary}
          disabled={define.isPending}
          onClick={() => void submit()}
          data-testid="save-scale"
        >
          {define.isPending ? "Saving…" : current ? "Replace the scale" : "Save the scale"}
        </button>
        <button type="button" className={styles.fixLink} onClick={onDone}>
          Not now
        </button>
      </p>
      <WriteFailed error={refusal} of="the scale" testId="scale-refused" />
    </div>
  );
}

// ----------------------------------------------------------------- assess again

export function RescoreForm({
  risk,
  scales,
  onDone,
}: {
  risk: Risk;
  scales: ReadonlyArray<RiskScale>;
  onDone: () => void;
}) {
  const live = scales.filter((scale) => !scale.superseded);
  const rescore = useRescoreRisk(risk.id);
  const [scaleId, setScaleId] = useState("");
  const [likelihood, setLikelihood] = useState("");
  const [consequence, setConsequence] = useState("");
  const [note, setNote] = useState("");
  const [refusal, setRefusal] = useState<unknown>(null);
  const [checks, attach] = useFormChecks<HTMLDivElement>();
  const scale = live.find((candidate) => candidate.id === (scaleId || live[0]?.id));

  async function submit() {
    setRefusal(null);
    const ok = checks.run([
      { field: "likelihood", ok: likelihood !== "", message: "Choose how likely it is now." },
      { field: "consequence", ok: consequence !== "", message: "Choose how bad it would be now." },
    ]);
    if (!ok || !scale) return;
    try {
      await rescore.mutateAsync({
        scale_id: scale.id,
        likelihood: Number(likelihood),
        consequence: Number(consequence),
        note: note.trim() === "" ? null : note.trim(),
      });
      onDone();
    } catch (error) {
      if (!checks.refuse(error)) setRefusal(error);
    }
  }

  if (live.length === 0) {
    return (
      <p className={styles.note} data-testid="no-scale-to-rescore">
        There is no current scoring scale to assess this risk on. An Admin defines one on the register.
      </p>
    );
  }

  return (
    <div className={own.form} ref={attach} data-testid="rescore-form">
      <p className={styles.note} data-testid="rescore-current">
        Now: {risk.score}. This adds a dated assessment; the earlier one stays in the history.
      </p>
      {live.length > 1 ? (
        <label className={styles.field}>
          <span>Scored against</span>
          <select
            aria-label="Scored against"
            value={scale?.id ?? ""}
            onChange={(event) => {
              setScaleId(event.target.value);
              setLikelihood("");
              setConsequence("");
            }}
          >
            {live.map((candidate) => (
              <option key={candidate.id} value={candidate.id}>
                {candidate.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      <label className={styles.field}>
        <span>How likely?</span>
        <select
          aria-label="How likely?"
          {...checks.field("likelihood")}
          value={likelihood}
          onChange={(event) => {
            checks.touch("likelihood");
            setLikelihood(event.target.value);
          }}
        >
          <option value="">Choose</option>
          {scale?.labels.map((label, index) => (
            <option key={label} value={index + 1}>
              {label}
            </option>
          ))}
        </select>
        <FieldError {...checks.say("likelihood")} />
      </label>
      <label className={styles.field}>
        <span>How bad, if it happened?</span>
        <select
          aria-label="How bad, if it happened?"
          {...checks.field("consequence")}
          value={consequence}
          onChange={(event) => {
            checks.touch("consequence");
            setConsequence(event.target.value);
          }}
        >
          <option value="">Choose</option>
          {scale?.labels.map((label, index) => (
            <option key={label} value={index + 1}>
              {label}
            </option>
          ))}
        </select>
        <FieldError {...checks.say("consequence")} />
      </label>
      <label className={styles.field}>
        <span>Why did it change? (optional)</span>
        <textarea
          aria-label="Why did it change?"
          rows={2}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder="The new barrier has been installed."
        />
      </label>
      <p className={own.rowActions}>
        <button
          type="button"
          className={styles.primary}
          disabled={rescore.isPending}
          onClick={() => void submit()}
          data-testid="save-rescore"
        >
          {rescore.isPending ? "Recording…" : "Record the assessment"}
        </button>
        <button type="button" className={styles.fixLink} onClick={onDone}>
          Not now
        </button>
      </p>
      <WriteFailed error={refusal} of="this risk" testId="rescore-refused" />
    </div>
  );
}

// ----------------------------------------------------------------- reopen

export function ReopenForm({ risk, onDone }: { risk: Risk; onDone: () => void }) {
  const reopen = useReopenRisk(risk.id);
  const [reason, setReason] = useState("");
  const [refusal, setRefusal] = useState<unknown>(null);
  const [checks, attach] = useFormChecks<HTMLDivElement>();

  async function submit() {
    setRefusal(null);
    const ok = checks.run([
      { field: "reason", ok: reason.trim() !== "", message: "Say why this risk is being reopened." },
    ]);
    if (!ok) return;
    try {
      await reopen.mutateAsync(reason.trim());
      onDone();
    } catch (error) {
      if (!checks.refuse(error)) setRefusal(error);
    }
  }

  return (
    <div className={own.form} ref={attach} data-testid="reopen-form">
      <label className={styles.field}>
        <span>Why is it open again?</span>
        <textarea
          aria-label="Why is it open again?"
          rows={2}
          {...checks.field("reason")}
          value={reason}
          onChange={(event) => {
            checks.touch("reason");
            setReason(event.target.value);
          }}
          placeholder="The defences failed an inspection."
        />
        <FieldError {...checks.say("reason")} />
      </label>
      <p className={own.rowActions}>
        <button
          type="button"
          className={styles.primary}
          disabled={reopen.isPending}
          onClick={() => void submit()}
          data-testid="save-reopen"
        >
          {reopen.isPending ? "Reopening…" : "Reopen it"}
        </button>
        <button type="button" className={styles.fixLink} onClick={onDone}>
          Not now
        </button>
      </p>
      <WriteFailed error={refusal} of="this risk" testId="reopen-refused" />
    </div>
  );
}

// ----------------------------------------------------------------- reword

/** Replace the "complete when" and "review if" texts. Only a text that changed is sent, so a form
 * opened to fix one never rewrites the other. */
export function RewordForm({ risk, onDone }: { risk: Risk; onDone: () => void }) {
  const reword = useRewordRisk(risk.id);
  const [criteria, setCriteria] = useState(risk.criteria_is_placeholder ? "" : risk.completion_criteria);
  const [trigger, setTrigger] = useState(risk.trigger_is_placeholder ? "" : risk.review_trigger);
  const [refusal, setRefusal] = useState<unknown>(null);
  const [checks, attach] = useFormChecks<HTMLDivElement>();

  async function submit() {
    setRefusal(null);
    const criteriaChanged = criteria.trim() !== risk.completion_criteria;
    const triggerChanged = trigger.trim() !== risk.review_trigger;
    const ok = checks.run([
      {
        field: "completion_criteria",
        ok: !criteriaChanged && !risk.criteria_is_placeholder ? true : criteriaRefusal(criteria) === null,
        message: criteriaRefusal(criteria) ?? "",
      },
      {
        field: "review_trigger",
        ok: !triggerChanged && !risk.trigger_is_placeholder ? true : triggerRefusal(trigger) === null,
        message: triggerRefusal(trigger) ?? "",
      },
      {
        field: "completion_criteria",
        ok: criteriaChanged || triggerChanged,
        message: "Change at least one of the two texts.",
      },
    ]);
    if (!ok) return;
    try {
      await reword.mutateAsync({
        ...(criteriaChanged ? { completion_criteria: criteria.trim() } : {}),
        ...(triggerChanged ? { review_trigger: trigger.trim() } : {}),
      });
      onDone();
    } catch (error) {
      if (!checks.refuse(error)) setRefusal(error);
    }
  }

  return (
    <div className={own.form} ref={attach} data-testid="reword-form">
      <label className={styles.field}>
        <span>Complete when?</span>
        <textarea
          aria-label="Complete when?"
          rows={2}
          {...checks.field("completion_criteria")}
          value={criteria}
          onChange={(event) => {
            checks.touch("completion_criteria");
            setCriteria(event.target.value);
          }}
          placeholder="Flood defences installed and signed off by the insurer."
        />
        <FieldError {...checks.say("completion_criteria")} />
      </label>
      <label className={styles.field}>
        <span>Review it if what happens?</span>
        <textarea
          aria-label="Review it if what happens?"
          rows={2}
          {...checks.field("review_trigger")}
          value={trigger}
          onChange={(event) => {
            checks.touch("review_trigger");
            setTrigger(event.target.value);
          }}
          placeholder="A new Environment Agency flood map for the area."
        />
        <FieldError {...checks.say("review_trigger")} />
      </label>
      <p className={own.rowActions}>
        <button
          type="button"
          className={styles.primary}
          disabled={reword.isPending}
          onClick={() => void submit()}
          data-testid="save-reword"
        >
          {reword.isPending ? "Saving…" : "Save the wording"}
        </button>
        <button type="button" className={styles.fixLink} onClick={onDone}>
          Not now
        </button>
      </p>
      <WriteFailed error={refusal} of="this risk" testId="reword-refused" />
    </div>
  );
}
